/**
 * Stripe wiring for the `v0-mx` card rail.
 *
 * The Mexican checkout offers a card charge next to the SPEI bank transfer, and that card charge
 * is the SAME integration the international flows run: the publishable key from
 * `window.config.stripePublicKey`, one lazily-loaded Stripe.js instance app-wide, and a
 * PaymentIntent client secret that arrives as the invoice's `provider_reference`.
 *
 * So nothing is re-implemented here. The loader lives in
 * [`@/utils/stripe`](../../../../utils/stripe.ts) (ONE Stripe.js instance across the Spark module,
 * every configurator flow, the wallet top-up and the product-detail modal), and the Elements
 * appearance lives with `configurator-v3`, which is where the studio's Stripe theme is authored —
 * the same cross-import the wallet and product-detail modals already use. Duplicating that
 * ~100-line rule map into each Mexican flow would only let the four checkouts drift apart
 * visually.
 *
 * What this flow does NOT share is invoice reconciliation: `reconcileSessionInvoice` in
 * `@/utils/stripe` assumes Stripe is the only rail and POSTs before it reads. The Mexican flows go
 * through [`./checkout-invoice`](./checkout-invoice.ts) instead, which reads first and understands
 * both providers.
 */
import React from 'react';

import { useThemeMode } from '@/nova/theme/OwsTheme';
import { useStripeAppearance as useStudioStripeAppearance } from '../../configurator-v3/data/payments';
import { MX_FIELD_BG } from '../steps/finish/spei.tokens';

export { isStripeLive, getStripe } from '@/utils/stripe';

/**
 * The studio's Stripe appearance, with TWO Mexican-checkout overrides. Both exist for the same
 * reason: here the Elements are nested inside an expanded payment-method card, whereas in
 * `v0`/`v3` they sit directly on the checkout pane.
 *
 * 1. **The inputs take a fill** instead of the international flows' transparent one, which would
 *    dissolve into the raised option card. The value comes from {@link MX_FIELD_BG}, the same
 *    pair the SPEI payer-name field uses.
 * 2. **Stripe's own accordion frame is flattened** — no border, no fill, no padding. Stripe wraps
 *    each payment method in a bordered, padded item; inside our option card that produced a
 *    visible box within a box, framing the card fields twice over. Our card IS the container, so
 *    Stripe's is stripped and its fields sit directly in it.
 *
 * Every value is passed as a concrete literal because Stripe renders Elements in a cross-origin
 * iframe that cannot resolve our `var(--ows-…)` custom properties. Everything else — the fonts,
 * the radii, the input borders, the light ⇄ dark flip — is inherited, so the four checkouts keep
 * looking like one product.
 */
export function useStripeAppearance(): ReturnType<typeof useStudioStripeAppearance> {
  const base = useStudioStripeAppearance();
  const mode = useThemeMode();
  return React.useMemo(
    () => ({
      ...base,
      rules: {
        ...base.rules,
        '.Input': { ...base.rules['.Input'], backgroundColor: MX_FIELD_BG[mode] },
        // Deliberately NOT spread from the base rule: the point is to drop the border and fill it
        // sets, and spreading them back in would leave the frame we are trying to remove.
        '.AccordionItem': {
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          boxShadow: 'none',
          padding: '0',
        },
      },
    }),
    [base, mode],
  );
}
